import {
  Component,
  OnInit,
  OnDestroy,
  inject,
  ViewContainerRef,
  ElementRef,
  Renderer2,
  ChangeDetectionStrategy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { BehaviorSubject, Observable, interval, take } from 'rxjs';
import { VersionService } from '../../../services/version/version.service';
import { SiteCacheService } from '../../../services/site-cache/site-cache.service';

@Component({
  selector: 'app-footer',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './footer.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./footer.component.scss'],
})
export class FooterComponent implements OnInit, OnDestroy {
  private versionService = inject(VersionService);
  private siteCacheService = inject(SiteCacheService);
  private viewContainerRef = inject(ViewContainerRef);
  private renderer = inject(Renderer2);
  private elementRef = inject(ElementRef);

  // Footer display properties
  versionDisplay$ = this.versionService.displayVersion$;
  showUpdateIndicator$ = this.versionService.updateAvailable$;

  // Popup state
  showVersionPopup = false;
  versionInfo: any = null;

  // Debug info
  currentTime = new Date();
  private timeInterval: any;

  constructor() {
    // Update current time every second for debugging
    this.timeInterval = setInterval(() => {
      this.currentTime = new Date();
    }, 1000);
  }

  ngOnInit(): void {
    // Component initialization logic
  }

  ngOnDestroy(): void {
    if (this.timeInterval) {
      clearInterval(this.timeInterval);
    }
    // Close popup if open
    if (this.showVersionPopup) {
      this.closeVersionPopup();
    }
  }

  /**
   * Handle version click - check for updates or show version info
   */
  onVersionClick(): void {
    // Use take(1) to prevent multiple subscriptions
    this.versionService.updateAvailable$
      .pipe(take(1))
      .subscribe((updateAvailable) => {
        if (updateAvailable) {
          // Handle update available
        } else {
          // Show version info popup
          this.showVersionInfo();
        }
      });
  }

  /**
   * Clear site cache completely
   */
  onClearCacheClick(): void {
    if (
      confirm(
        'Are you sure you want to clear all site cache? This will reload the page.'
      )
    ) {
      this.siteCacheService.performCompleteCacheFlush();
    }
  }

  /**
   * Close version popup
   */
  closeVersionPopup(): void {
    this.showVersionPopup = false;
    this.versionInfo = null;

    // Remove popup from DOM if it exists
    const existingPopup = document.querySelector('.version-popup-overlay');
    if (existingPopup) {
      existingPopup.remove();
    }
  }

  /**
   * Show detailed version information for debugging
   */
  public async showVersionInfo(): Promise<void> {
    try {
      // Show popup immediately to prevent double-click issues
      this.showVersionPopup = true;

      // Load version info automatically
      await this.loadVersionInfo();

      // Create popup in body to avoid footer constraints
      this.createPopupInBody();
    } catch (error) {
      console.error('[FooterComponent] Error showing version info:', error);
    }
  }

  /**
   * Load version information data
   */
  private async loadVersionInfo(): Promise<void> {
    try {
      const versionInfo = await this.versionService.getVersionInfo();

      this.versionInfo = versionInfo;
    } catch (error) {
      console.error('[FooterComponent] Error getting version info:', error);
      // Show error info instead
      this.versionInfo = {
        displayVersion: 'Error',
        backendVersion: 'Error',
        serviceWorkerVersion: 'Error',
        timestamp: new Date(),
      };
    }
  }

  /**
   * Create popup directly in body to avoid footer DOM constraints
   */
  private createPopupInBody(): void {
    // Remove existing popup if any
    const existingPopup = document.querySelector('.version-popup-overlay');
    if (existingPopup) {
      existingPopup.remove();
    }

    // Create popup HTML
    const popupHTML = `
      <div class="version-popup-overlay" id="version-popup-overlay">
        <div class="version-popup">
          <div class="popup-header">
            <h3>Version Information</h3>
            <button class="close-btn" id="popup-close-btn">×</button>
          </div>
          
          <div class="popup-content">
            ${
              this.versionInfo
                ? `
              <div class="info-content">
                <div class="info-row">
                  <span class="label">Version:</span>
                  <span class="value">${this.versionInfo.displayVersion}</span>
                </div>
                <div class="info-row">
                  <span class="label">Backend:</span>
                  <span class="value">${this.versionInfo.backendVersion}</span>
                </div>
                <div class="info-row">
                  <span class="label">Service Worker:</span>
                  <span class="value">${
                    this.versionInfo.serviceWorkerVersion
                  }</span>
                </div>
                <div class="info-row">
                  <span class="label">Last checked:</span>
                  <span class="value">${this.versionInfo.timestamp.toLocaleString()}</span>
                </div>
              </div>
            `
                : `
              <div class="loading-content">
                <p>Loading version information...</p>
                <p>Please wait...</p>
              </div>
            `
            }
          </div>
          
          <div class="popup-actions">
            <button class="cache-clear-btn" id="popup-cache-btn" title="Clear all site cache and reload">
              🗑️ Clear Site Cache
            </button>
          </div>
        </div>
      </div>
    `;

    // Append to body
    document.body.insertAdjacentHTML('beforeend', popupHTML);

    // Add event listeners
    const overlay = document.getElementById('version-popup-overlay');
    const closeBtn = document.getElementById('popup-close-btn');
    const cacheBtn = document.getElementById('popup-cache-btn');

    if (overlay) {
      overlay.addEventListener('click', (e) => {
        if (e.target === overlay) {
          this.closeVersionPopup();
        }
      });
    }

    if (closeBtn) {
      closeBtn.addEventListener('click', () => {
        this.closeVersionPopup();
      });
    }

    if (cacheBtn) {
      cacheBtn.addEventListener('click', () => {
        this.onClearCacheClick();
      });
    }
  }
}
